import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';

const CHAT_DISABLED_MESSAGE = '目前暫時無法建立新的對話，請稍後再試。';

export const DIRECT_CONVERSATION_ERROR_KEYS = Object.freeze({
  UNAVAILABLE: 'travel.direct.errors.unavailable',
  PRIVACY_DENIED: 'travel.direct.errors.privacyDenied',
  CHAT_DISABLED: 'travel.direct.errors.chatDisabled',
  ACCOUNT_CANNOT_USE: 'travel.direct.errors.accountCannotUse',
  PEER_NOT_FOUND: 'travel.direct.errors.peerNotFound',
  AUTH_EXPIRED: 'travel.direct.errors.authExpired',
  NETWORK: 'travel.direct.errors.network',
  START_FAILED: 'travel.direct.errors.startFailed',
});

export function imErrorKey(error) {
  return String(error?.raw?.key || '').toLowerCase();
}

export function imErrorCode(error) {
  return error?.code ?? error?.raw?.code;
}

export function isChatDisabledError(error) {
  return imErrorKey(error) === 'im.chat.disabled' || imErrorCode(error) === 91148;
}

export function isAuthExpiredError(error) {
  const key = imErrorKey(error);
  const code = imErrorCode(error);
  return key === 'token.expired' || key === 'token.not.found' || code === 90005 || code === 90003 || code === 401;
}

function isPeerNotFoundError(error) {
  const key = imErrorKey(error);
  const code = imErrorCode(error);
  return key === 'member.not.exist' || code === 90019;
}

/** 後端錯誤碼／key → 穩定文案 key；文案在呈現層依目前語系翻譯。 */
export function classifyDirectConversationError(error) {
  const key = imErrorKey(error);
  const code = imErrorCode(error);
  if (isChatDisabledError(error)) return DIRECT_CONVERSATION_ERROR_KEYS.CHAT_DISABLED;
  if (key === 'im.blocked' || code === 91080) return DIRECT_CONVERSATION_ERROR_KEYS.UNAVAILABLE;
  if (key === 'im.privacy.direct.denied' || code === 91081) return DIRECT_CONVERSATION_ERROR_KEYS.PRIVACY_DENIED;
  if (key === 'parameter.not.legal' || code === 90002) return DIRECT_CONVERSATION_ERROR_KEYS.UNAVAILABLE;
  if (key === 'permissions.insufficient.error' || code === 90007) {
    return DIRECT_CONVERSATION_ERROR_KEYS.ACCOUNT_CANNOT_USE;
  }
  if (isPeerNotFoundError(error)) return DIRECT_CONVERSATION_ERROR_KEYS.PEER_NOT_FOUND;
  if (isAuthExpiredError(error)) return DIRECT_CONVERSATION_ERROR_KEYS.AUTH_EXPIRED;
  if (!error?.raw && /network|timeout|failed/i.test(String(error?.message || ''))) {
    return DIRECT_CONVERSATION_ERROR_KEYS.NETWORK;
  }
  return DIRECT_CONVERSATION_ERROR_KEYS.START_FAILED;
}

function resolveDirectCopy(messageKey, t) {
  const translateFn = typeof t === 'function' ? t : (k) => translate(DEFAULT_LOCALE, k);
  const label = translateFn(messageKey);
  if (typeof label === 'string' && label && label !== messageKey) return label;
  return translate(DEFAULT_LOCALE, messageKey);
}

export function directConversationErrorMessage(error, t) {
  const classified = classifyDirectConversationError(error);
  if (classified !== DIRECT_CONVERSATION_ERROR_KEYS.START_FAILED) {
    return resolveDirectCopy(classified, t);
  }
  if (error?.message) return error.message;
  return resolveDirectCopy(classified, t);
}

export function threadErrorMessage(error) {
  if (isChatDisabledError(error)) return CHAT_DISABLED_MESSAGE;
  if (isAuthExpiredError(error)) return '登入已過期，請重新登入後再試。';
  if (imErrorKey(error) === 'im.blocked' || imErrorCode(error) === 91080) {
    return '目前無法傳送訊息。';
  }
  if (imErrorKey(error) === 'im.conversation.not_writable' || imErrorCode(error) === 91149) {
    return '聊天室目前為唯讀，無法傳送訊息。';
  }
  return error?.message || '訊息無法送出，請稍後再試。';
}

export function reactionErrorMessage(error) {
  if (isAuthExpiredError(error)) return '登入已過期，請重新登入後再試。';
  if (imErrorKey(error) === 'im.conversation.not_writable' || imErrorCode(error) === 91149) {
    return '聊天室目前為唯讀，無法回應訊息。';
  }
  if (imErrorKey(error) === 'im.reaction.limit.reached' || imErrorCode(error) === 91147) {
    return '這則訊息的表情種類已達上限。';
  }
  if (imErrorKey(error) === 'entity.not.found' || imErrorCode(error) === 90014) {
    return '找不到這則訊息。';
  }
  if (imErrorKey(error) === 'permissions.insufficient.error' || imErrorCode(error) === 90007) {
    return '目前無法對這則訊息做出回應。';
  }
  if (imErrorKey(error) === 'parameter.not.legal' || imErrorCode(error) === 90002) {
    return '無法對這則訊息做出回應。';
  }
  return error?.message || '無法更新回應，請稍後再試。';
}

export function editErrorMessage(error) {
  if (isAuthExpiredError(error)) return '登入已過期，請重新登入後再試。';
  if (imErrorKey(error) === 'im.conversation.not_writable' || imErrorCode(error) === 91149) {
    return '聊天室目前為唯讀，無法編輯訊息。';
  }
  if (imErrorKey(error) === 'entity.not.found' || imErrorCode(error) === 90014) {
    return '找不到這則訊息。';
  }
  if (imErrorKey(error) === 'permissions.insufficient.error' || imErrorCode(error) === 90007) {
    return '目前無法編輯這則訊息。';
  }
  if (imErrorKey(error) === 'parameter.not.legal' || imErrorCode(error) === 90002) {
    return '這則訊息無法編輯。';
  }
  return error?.message || '訊息無法編輯，請稍後再試。';
}

export function revokeErrorMessage(error) {
  if (isAuthExpiredError(error)) return '登入已過期，請重新登入後再試。';
  if (imErrorKey(error) === 'im.conversation.not_writable' || imErrorCode(error) === 91149) {
    return '聊天室目前為唯讀，無法撤回訊息。';
  }
  if (imErrorKey(error) === 'entity.not.found' || imErrorCode(error) === 90014) {
    return '找不到這則訊息。';
  }
  if (imErrorKey(error) === 'permissions.insufficient.error' || imErrorCode(error) === 90007) {
    return '目前無法撤回這則訊息。';
  }
  if (imErrorKey(error) === 'parameter.not.legal' || imErrorCode(error) === 90002) {
    return '這則訊息無法撤回。';
  }
  return error?.message || '訊息無法撤回，請稍後再試。';
}

export { CHAT_DISABLED_MESSAGE };
